import type {
  ExternalRefUsageItemResponse,
  ExternalRefUsageItemViewModel,
  ExternalRefCapabilityUsageResponse,
  ExternalRefCapabilityUsageViewModel,
  BatchJobSummaryResponse,
  BatchJobSummaryViewModel,
  MonitoringUsageItemResponse,
  MonitoringProcessViewModel,
  MonitoringMappingJobViewModel,
  SearchUsageViewModel,
  ProcessSummaryResponse,
  ProcessSummaryViewModel,
  ProcessChartDataPointResponse,
  ProcessChartDataPointViewModel,
  ProcessTimeSeriesResponse,
  ProcessTimeSeriesViewModel,
  CostEventResponse,
  CostEventViewModel,
} from "@models/monitoring";
import type { PaginatedResponse } from "@models/common";

export const buildExternalRefUsageItemViewModel = (
  response: ExternalRefUsageItemResponse,
): ExternalRefUsageItemViewModel => {
  return {
    externalRefType: response.external_ref_type,
    externalRefId: response.external_ref_id,
    userFullName: response.user_full_name,
    userExternalId: response.user_external_id,
    organisationName: response.organisation_name,
    organisationExternalId: response.organisation_external_id,
    totalCost: response.total_cost,
    eventCount: response.event_count,
    usersCount: response.users_count,
    firstUsed: new Date(response.first_used),
    lastUsed: new Date(response.last_used),
    currency: response.currency,
  };
};

export const buildPaginatedExternalRefUsageViewModel = (
  response: PaginatedResponse<ExternalRefUsageItemResponse>,
): PaginatedResponse<ExternalRefUsageItemViewModel> => {
  return {
    items: response.items.map(buildExternalRefUsageItemViewModel),
    total: response.total,
    limit: response.limit,
    offset: response.offset,
    has_more: response.has_more,
  };
};

export const buildProcessSummaryViewModel = (
  response: ProcessSummaryResponse,
): ProcessSummaryViewModel => {
  return {
    totalProcesses: response.total_processes,
    completedProcesses: response.completed_processes,
    failedProcesses: response.failed_processes,
    pendingProcesses: response.pending_processes,
    percentage: response.percentage,
    dateRange: response.date_range
      ? {
          startDate: response.date_range.start_date
            ? new Date(response.date_range.start_date)
            : null,
          endDate: response.date_range.end_date
            ? new Date(response.date_range.end_date)
            : null,
        }
      : null,
  };
};

export const buildProcessChartDataPointViewModel = (
  response: ProcessChartDataPointResponse,
): ProcessChartDataPointViewModel => {
  return {
    timestamp: new Date(response.timestamp),
    totalProcesses: response.total_processes,
    completedProcesses: response.completed_processes,
    failedProcesses: response.failed_processes,
    percentage: response.percentage,
  };
};

export const buildProcessTimeSeriesViewModel = (
  response: ProcessTimeSeriesResponse,
): ProcessTimeSeriesViewModel => {
  return {
    dataPoints: response.data_points.map(buildProcessChartDataPointViewModel),
    dateRange: {
      startDate: new Date(response.date_range.start_date),
      endDate: new Date(response.date_range.end_date),
    },
  };
};

export const buildMonitoringProcessViewModelFromUsage = (
  response: MonitoringUsageItemResponse,
): MonitoringProcessViewModel => {
  const processId =
    response.entity_id ?? response.external_ref_type.replace("PROCESS#", "");

  return {
    id: processId,
    idShort: processId.substring(0, 8),
    status: response.status ?? "UNKNOWN",
    numberOfItems: response.no_process_items ?? "0/0",
    totalCost: response.total_cost,
    currency: response.currency,
    eventCount: response.event_count,
    usersCount: response.users_count,
    createdAt: response.created_at ? new Date(response.created_at) : new Date(),
    createdBy: response.user_full_name ?? response.created_by_name ?? null,
    organisationName: response.organisation_name ?? null,
    doctypeCode: response.doctype_code ?? null,
    doctypeName: response.doctype_name ?? null,
  };
};

export const buildBatchJobSummaryViewModel = (
  response: BatchJobSummaryResponse,
): BatchJobSummaryViewModel => {
  const createdBy = response.created_by;
  const fullName = createdBy
    ? [createdBy.first_name, createdBy.last_name]
        .filter((part): part is string => Boolean(part))
        .join(" ")
        .trim() ||
      createdBy.email ||
      null
    : null;

  return {
    jobId: response.job_id,
    pluginId: response.plugin_id,
    service: response.service,
    status: response.status,
    inputKind: response.input_kind,
    filename: response.filename,
    rowsTotal: response.rows_total,
    rowsDone: response.rows_done,
    error: response.error ?? null,
    createdBy: fullName,
    createdAt: new Date(response.created_at),
    updatedAt: new Date(response.updated_at),
  };
};

export const buildSearchUsageViewModels = (
  items: ExternalRefUsageItemViewModel[],
): SearchUsageViewModel[] =>
  items.map((item) => {
    // external_ref_type is "SEARCH#<correlation_id>" — the correlation id is the
    // request id. Each row is already one search request (backend groups by
    // external_ref_type), so no aggregation: render who / when per request.
    const requestId = item.externalRefType.replace(/^SEARCH#/, "");
    return {
      requestId,
      requestIdShort: requestId.substring(0, 8),
      createdBy: item.userFullName,
      usedAt: item.firstUsed,
      eventCount: item.eventCount,
      totalCost: item.totalCost,
      currency: item.currency,
      organisationName: item.organisationName,
    };
  });

/**
 * Build a mapping job view model from a single already-enriched BFF row
 * (core's /cost-events/monitoring/mapping-usage). No separate job-metadata
 * lookup needed — usage and job fields arrive merged.
 */
export const buildMonitoringMappingJobViewModelFromUsage = (
  response: MonitoringUsageItemResponse,
): MonitoringMappingJobViewModel => {
  const jobId =
    response.entity_id ?? response.external_ref_type.replace("JOB#", "");

  return {
    jobId,
    jobIdShort: jobId.substring(0, 8),
    status: response.status ?? "UNKNOWN",
    pluginId: response.plugin_id ?? null,
    rowsTotal: response.rows_total ?? null,
    rowsDone: response.rows_done ?? null,
    totalCost: response.total_cost,
    currency: response.currency,
    eventCount: response.event_count,
    usersCount: response.users_count,
    createdAt: response.created_at
      ? new Date(response.created_at)
      : new Date(),
    createdBy: response.user_full_name ?? response.created_by_name ?? null,
    organisationName: response.organisation_name ?? null,
  };
};

export const buildCostEventViewModel = (
  response: CostEventResponse,
): CostEventViewModel => {
  return {
    id: response.id,
    occurredAt: new Date(response.occurred_at),
    serviceName: response.service_name,
    capability: response.capability,
    modelKey: response.model_key,
    inputTokens: response.input_tokens,
    outputTokens: response.output_tokens,
    totalTokens: response.total_tokens,
    pagesProcessed: response.pages_processed,
    costAmount: response.cost_amount,
    currency: response.currency,
  };
};

export const buildExternalRefCapabilityUsageViewModel = (
  response: ExternalRefCapabilityUsageResponse,
): ExternalRefCapabilityUsageViewModel => ({
  capability: response.capability,
  eventCount: response.event_count,
  totalCost: response.total_cost,
  currency: response.currency,
  inputCost: response.input_cost,
  outputCost: response.output_cost,
  inputTokens: response.input_tokens,
  outputTokens: response.output_tokens,
  totalTokens: response.total_tokens,
  pagesProcessed: response.pages_processed,
});
